import React, { useEffect, useLayoutEffect, useRef, useState } from "react"
import dayjs from "dayjs"
import Link from "next/link"
import type {
  ChatRoomStatus,
  DunningCaseItem,
  DunningChatMessage,
  DunningContactLogEntry,
  DunningDraftContentConsistency,
  DunningMessageDraft,
} from "../types"
import { useDunningChatMessages } from "../hooks/useDunningChatMessages"
import { useDunningMessageDrafts } from "../hooks/useDunningMessageDrafts"
import {
  CONTACT_CHANNEL_LABELS,
  DAILY_CONTACT_LIMIT,
  formatJapaneseDateTime,
  isPromiseScheduleActive,
} from "./shared"
import { DraftDecisionLog } from "./DraftDecisionLog"
import { useSmoothApi } from "src/hooks/useSmoothApi"
import { SMOOTH_LINE_API_URL } from "src/lib/constants"
import useSWR from "swr"

/**
 * messagesを正本とする全履歴を表示し、督促送信分を色分けする。
 * 送信はケース状態 / 年末年始などのガードで4xxが返るため
 * サーバのメッセージをそのまま表示する。
 *
 * AIドラフト: 生成（POST message_drafts）→ コンポーザーで編集 →
 * 送信時に PATCH（editedBody 保存）→ POST :draft_id/send（通常送信と同じガード・接触カウント）。
 * 再生成は新しい idempotencyKey での POST（旧ドラフトは superseded になる）。
 */

function senderLabel(message: DunningChatMessage) {
  return message.senderName ?? "自動送信"
}

/** API が整形した単一・一括送信の本文を保持し、本文のないメディアだけをプレースホルダ化する。 */
function displayMessageBody(message: DunningChatMessage) {
  return typeof message.body === "string" ? message.body : `（${message.contentType}）`
}

/** 本日の内訳の各行に付ける印。送達不明は数に入れたまま、担当者が上限に達した理由を分かるように示す。 */
function todayContactMarker(entry: DunningContactLogEntry) {
  const markers = entry.countsAsContact
    ? [entry.sessionContinuation ? "同一接触" : null, entry.deliveryUnconfirmed ? "送達不明" : null]
    : ["対象外"]
  const labels = markers.filter((marker): marker is string => marker != null)
  return labels.length > 0 ? `（${labels.join("・")}）` : ""
}

/**
 * AIドラフトへの 👍/👎 評価（プロンプト改善の教師データ）。
 * 同じボタンをもう一度押すと取り消し（feedback: null）。
 */
const DraftFeedbackButtons: React.FC<{
  draft: DunningMessageDraft
  disabled?: boolean
  onSelect: (feedback: "good" | "bad" | null) => void
}> = ({ draft, disabled, onSelect }) => (
  <span
    className="flex items-center gap-1"
    title="AI文面の評価（プロンプト改善の教師データになります）"
  >
    {(["good", "bad"] as const).map((value) => {
      const active = draft.feedback === value
      return (
        <button
          key={value}
          type="button"
          disabled={disabled}
          aria-pressed={active}
          aria-label={value === "good" ? "良い文面として評価" : "悪い文面として評価"}
          onClick={() => onSelect(active ? null : value)}
          className={`rounded-full border px-2 py-0.5 text-[1.15rem] transition-colors disabled:opacity-50 ${
            active
              ? value === "good"
                ? "border-emerald-300 bg-emerald-50"
                : "border-red-300 bg-red-50"
              : "border-gray-200 bg-white opacity-45 grayscale hover:opacity-100 hover:grayscale-0"
          }`}
        >
          {value === "good" ? "👍" : "👎"}
        </button>
      )
    })}
  </span>
)

/** チャットステータス（chat_rooms.status）の表示ラベル・バッジ色 */
const CHAT_STATUS_LABELS: Record<ChatRoomStatus, string> = {
  _none: "なし",
  demanded: "督促済",
  replied: "返信済",
  undemanding: "未督促",
  action_required: "要対応",
}

const CHAT_STATUS_CLASSES: Record<ChatRoomStatus, string> = {
  _none: "border-gray-300 bg-gray-100 text-black",
  demanded: "border-blue-900 bg-blue-900 text-white",
  replied: "border-blue-500 bg-blue-500 text-white",
  undemanding: "border-slate-500 bg-slate-500 text-white",
  action_required: "border-red-500 bg-red-500 text-white",
}

const CHAT_STATUS_ORDER: ChatRoomStatus[] = [
  "_none",
  "demanded",
  "replied",
  "undemanding",
  "action_required",
]

const DRAFT_STATUS_LABELS: Record<DunningMessageDraft["status"], string> = {
  current: "下書き",
  sending: "送信中",
  superseded: "旧版",
  discarded: "破棄済み",
  sent: "送信済",
}

const DRAFT_STATUS_CLASSES: Record<DunningMessageDraft["status"], string> = {
  current: "bg-yellow-100 text-yellow-700",
  sending: "bg-blue-100 text-blue-700",
  superseded: "bg-gray-100 text-black",
  discarded: "bg-gray-100 text-black",
  sent: "bg-green-100 text-green-700",
}

/**
 * 本文の金額・日付に入力の裏付けが無いドラフトの注意書き。観測前の旧ドラフトは出さない。
 * 観測は生成時の本文に対するものなので、編集後は「生成時の本文」の結果であることを明示する。
 */
const contentConsistencyNotice = (
  observation: DunningDraftContentConsistency | undefined,
  edited: boolean
): string | null => {
  if (!observation || observation.status === "valid") return null
  const editedSuffix = "。編集後の本文は確認していません"
  if (observation.status === "error") {
    return edited
      ? `生成時の本文の金額・日付の確認に失敗していました${editedSuffix}`
      : "本文の金額・日付の確認に失敗しました。このドラフトは自動送信されません"
  }
  const values = [
    ...(observation.unsupportedAmounts ?? []).map((amount) => `${amount.toLocaleString()}円`),
    ...(observation.unsupportedDates ?? []).map((date) => dayjs(date).format("M月D日")),
  ].join("、")
  return edited
    ? `生成時の本文に入力に見当たらない金額・日付がありました（${values}）${editedSuffix}`
    : `入力に見当たらない金額・日付があります（${values}）。このドラフトは自動送信されません`
}

/**
 * 入力欄の高さ上限を決めるときに操作行の下へ残す余白（px）。
 * 入力欄の枠の py-3（下 7.5px）+ 枠の下の注記の mt-2（5px）+ コンポーザー外枠の py-4（下 10px）。
 * html の font-size が 62.5%（1rem = 10px）のため Tailwind の spacing は 1 = 2.5px で換算する。
 */
const COMPOSER_BOTTOM_MARGIN = 24

/** 督促チャットの吹き出しの形と色（お客様は白・当社は水色）。AIドラフト検証の会話編集の吹き出しでも使う */
export const chatBubbleClass = (direction: DunningChatMessage["direction"]) =>
  direction === "inbound"
    ? "rounded-2xl rounded-bl-[4px] border border-gray-200 bg-white text-black"
    : "rounded-2xl rounded-br-[4px] bg-gradient-to-tr from-[#6EC3D8] to-[#5BA7BA] text-white"

/** 督促チャットの吹き出し1件。AIドラフト検証の架空ケースの会話表示でも使う */
export const Bubble: React.FC<{ message: DunningChatMessage }> = ({ message }) => {
  const isInbound = message.direction === "inbound"
  const displayBody = displayMessageBody(message)
  const bubbleClass = chatBubbleClass(message.direction)

  return (
    <div
      className={`flex max-w-[78%] flex-col ${isInbound ? "items-start self-start" : "items-end self-end"}`}
    >
      <div
        className={`whitespace-pre-wrap break-words px-4 py-2.5 text-[1.4rem] leading-relaxed shadow-sm ${bubbleClass}`}
      >
        {message.contentType === "image" && message.previewImagePath ? (
          <ChatImage message={message} />
        ) : (
          displayBody
        )}
      </div>
      <div className="px-1 pt-0.5 text-black">
        {!isInbound && (
          <>
            <span className="text-[1.125rem] font-normal text-black">
              {senderLabel(message)} ・{" "}
            </span>
          </>
        )}
        <span className="text-[1.125rem] font-normal text-black">
          {formatJapaneseDateTime(message.sentAt)}
        </span>
        {message.isDunning &&
          message.countsAsContact &&
          (message.continuesContactSession ? (
            <span
              className="ml-1 rounded-full bg-gray-100 px-1.5 font-medium text-black"
              title="10分以内の連続送信（最大3通）は直前の接触と同一カウントです"
            >
              同一接触
            </span>
          ) : (
            <span className="ml-1 rounded-full bg-red-100 px-1.5 font-semibold text-red-700">
              接触+1
            </span>
          ))}
      </div>
    </div>
  )
}

/** APIが返した値のうち http(s) URL のみ表示に使う（データ欠損や想定外スキームは破棄） */
const toSafeImageUrl = (value: unknown): string | null =>
  typeof value === "string" && /^https?:\/\//.test(value) ? value : null

const ChatImage: React.FC<{ message: DunningChatMessage }> = ({ message }) => {
  const { getWithToken } = useSmoothApi()
  const [unrecoverable, setUnrecoverable] = useState(false)
  const retriedRef = useRef(false)
  const [originalUrl, setOriginalUrl] = useState<string | null>(null)
  const [originalError, setOriginalError] = useState(false)
  const [opening, setOpening] = useState(false)
  const dialogRef = useRef<HTMLDialogElement>(null)

  useEffect(() => {
    if (originalUrl) dialogRef.current?.showModal()
  }, [originalUrl])

  const fetchImageUrl = async (path: string | null | undefined) => {
    if (!path) return null
    const response = await getWithToken(`${SMOOTH_LINE_API_URL}${path}`)
    return toSafeImageUrl(response.data?.image_url)
  }

  // 表示時に取得するのは preview のみ（original はクリック時に都度取得し、リクエスト数と失効の両方を避ける）
  const imageSWR = useSWR(
    message.previewImagePath ? `dunning-chat-image-${message.messageId}` : null,
    () => fetchImageUrl(message.previewImagePath).catch(() => null),
    // image_url は S3 の presigned URL（既定約15分で失効）のことがある。
    // フォーカス時の一斉再検証ではなく、表示に失敗した画像だけ onError → mutate で取り直す。
    { revalidateOnFocus: false }
  )

  const openOriginalImage = async () => {
    setOpening(true)
    const url = await fetchImageUrl(message.originalImagePath).catch(() => null)
    if (url) {
      setOriginalUrl(url)
      setOriginalError(false)
    }
    else setOriginalError(true)
    setOpening(false)
  }

  if (imageSWR.isLoading) return <>（画像を読み込み中...）</>

  const previewImageUrl = imageSWR.data
  if (!previewImageUrl || unrecoverable) return <>（画像を取得できません）</>

  return (
    <>
      <button
        type="button"
        aria-label="画像を拡大する"
        aria-busy={opening}
        disabled={opening}
        className="block"
        onClick={openOriginalImage}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={previewImageUrl}
          alt={message.direction === "inbound" ? "ユーザー送信画像" : "オペレーター送信画像"}
          className="max-h-64 max-w-full rounded-lg object-contain"
          onLoad={() => {
            retriedRef.current = false
          }}
          onError={async () => {
            // presigned URL の失効など。1回だけ再取得し、それでも表示できなければ代替表示に切り替える
            if (retriedRef.current) {
              setUnrecoverable(true)
              return
            }
            retriedRef.current = true
            const failedUrl = previewImageUrl
            const refreshedUrl = await imageSWR.mutate().catch(() => null)
            // 固定URL（contentProvider が line 以外）は再取得しても変わらず onError も再発火しないため、
            // 同一URLならこの時点で代替表示に落とす
            if (!refreshedUrl || refreshedUrl === failedUrl) setUnrecoverable(true)
          }}
        />
      </button>
      {originalError && !originalUrl && <p role="alert">画像を取得できませんでした。もう一度お試しください。</p>}
      {originalUrl && (
        <dialog
          ref={dialogRef}
          aria-label="チャット画像"
          className="m-auto max-h-[95dvh] max-w-[95vw] rounded-xl bg-white p-4 backdrop:bg-black/60"
          onClose={() => {
            setOriginalUrl(null)
            setOriginalError(false)
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) dialogRef.current?.close()
          }}
        >
          <button
            type="button"
            autoFocus
            onClick={() => dialogRef.current?.close()}
            className="mb-3 rounded border px-4 py-2"
          >
            閉じる
          </button>
          {originalError ? (
            <div>
              <p role="alert">画像を取得できませんでした。もう一度お試しください。</p>
              <button
                type="button"
                disabled={opening}
                onClick={openOriginalImage}
                className="mt-3 rounded border px-4 py-2"
              >
                {opening ? "読み込み中…" : "画像を再読み込み"}
              </button>
            </div>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={originalUrl}
              alt="チャットの拡大画像"
              className="max-h-[80dvh] max-w-full object-contain"
              onError={() => setOriginalError(true)}
            />
          )}
        </dialog>
      )}
    </>
  )
}

type Props = {
  caseItem: DunningCaseItem
  /** 送信成功後にケース詳細（本日接触・コンタクトログ・タイムライン）を再取得する */
  onSent?: () => void
  /** いま送ると非カウントになるか（自動非カウント・事務連絡のチェック）を送信前ゲートの表示に伝える */
  onNonCountingSendChange?: (nonCountingSend: boolean) => void
}

export const DunningChatPanel: React.FC<Props> = ({ caseItem, onSent, onNonCountingSendChange }) => {
  const {
    messages,
    hasOlder,
    loadOlder,
    loadingOlder,
    olderError,
    chatStatus,
    isLoading,
    error,
    sendMessage,
    isSending,
    sendError,
    clearSendError,
    refresh: refreshMessages,
    updateChatStatus,
    isUpdatingChatStatus,
    chatStatusError,
    setAwaitingReplyActive,
    isUpdatingAwaitingReply,
    updateAwaitingReplyError,
  } = useDunningChatMessages(caseItem.id, caseItem.dunningCase.status)
  const {
    drafts,
    humanReview,
    discard,
    isHistoryLoading,
    refresh: refreshDrafts,
    generate,
    saveEditedBody,
    setFeedback,
    sendDraft,
    isGenerating,
    isSendingDraft,
    draftError,
  } = useDunningMessageDrafts(caseItem.id)

  const [isDiscarding, setIsDiscarding] = useState(false)
  const [isSubmittingDraft, setIsSubmittingDraft] = useState(false)
  const [draft, setDraft] = useState("")
  const [activeDraft, setActiveDraft] = useState<DunningMessageDraft | null>(null)
  const [humanReviewReason, setHumanReviewReason] = useState<string | null>(null)
  const displayedHumanReviewReason = humanReviewReason ?? humanReview?.reason ?? null
  const [discardedDraftIds, setDiscardedDraftIds] = useState<Set<number>>(() => new Set())
  const hasAttemptedDraftRestoreRef = useRef(false)
  /**
   * 架電内容の事務連絡フラグ（countsAsContact: false で送信）。
   * 誤爆防止のため送信成功後は必ず false に戻す。AIドラフトの送信（電話後の案内など）にも使える。
   */
  const [isClericalNotice, setIsClericalNotice] = useState(false)
  const scrollRef = useRef<HTMLDivElement>(null)
  const composerRef = useRef<HTMLTextAreaElement>(null)
  const composerActionsRef = useRef<HTMLDivElement>(null)
  const resizeComposerRef = useRef<(followCaret: boolean) => void>(() => undefined)
  const restoreScrollFrameRef = useRef<number | null>(null)
  const previousDraftRef = useRef(draft)

  const status = caseItem.dunningCase.status
  const supportedStatus =
    status === "open" ||
    status === "paused" ||
    status === "closed" ||
    status === "resolved" ||
    status === "monitoring"
  const unsupportedStatus = !supportedStatus
  const isClericalOnlyStatus = status === "resolved" || status === "monitoring"
  const promise = caseItem.facts?.promise
  const promiseScheduledOn = promise?.scheduledOn
  const scheduledPromise = promiseScheduledOn ? dayjs(promiseScheduledOn) : null
  const hasActivePromise = scheduledPromise?.isValid()
    ? isPromiseScheduleActive(scheduledPromise)
    : promise?.active === true
  const automaticallyNonCounting =
    status === "closed" || status === "paused" || isClericalOnlyStatus || hasActivePromise
  const draftBlockedByNonCounting = activeDraft !== null && automaticallyNonCounting
  const draftContentNotice = contentConsistencyNotice(
    activeDraft?.generationMetadata.contentConsistency,
    activeDraft !== null && draft.trim() !== activeDraft.body.trim()
  )
  const lineBlocked = caseItem.facts?.user?.lineBlocked ?? false
  // submit() の nonCounting と同じ判定。送信前ゲートはこの値で「事務連絡を送れるか」を表示する
  const nonCountingSend = !unsupportedStatus && (automaticallyNonCounting || isClericalNotice)
  useEffect(() => {
    onNonCountingSendChange?.(nonCountingSend)
  }, [nonCountingSend, onNonCountingSendChange])
  const todayCount = caseItem.facts?.contact?.todayCount
  const todayContacts = caseItem.facts?.contact?.today ?? []

  const historyScroll = useRef<{ first?: number; height: number; top: number }>({
    height: 0,
    top: 0,
  })
  useLayoutEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const first = messages[0]?.messageId
    const previous = historyScroll.current
    const prepended =
      previous.first != null &&
      first !== previous.first &&
      messages.some((message) => message.messageId === previous.first)
    if (prepended) el.scrollTop = previous.top + el.scrollHeight - previous.height
    else el.scrollTop = el.scrollHeight
    historyScroll.current = { first, height: el.scrollHeight, top: el.scrollTop }
  }, [messages.length])

  // 履歴と入力欄の上端を動かさず、下方向へ伸ばす。上限は「可視ビューポート高」から決め、
  // スクロール位置には依存させない（依存させると、送信操作を画面内に入れた直後に上限が
  // 現在の高さへ収束して伸びなくなり、既定のスクロール位置では3行に張り付く）。
  // 下端を越えた本文は textarea 内でスクロールし、caret 追従による親の移動を防ぐ。
  useLayoutEffect(() => {
    const el = composerRef.current
    if (!el) return

    const resize = (followCaret: boolean) => {
      if (restoreScrollFrameRef.current != null) {
        window.cancelAnimationFrame(restoreScrollFrameRef.current)
        restoreScrollFrameRef.current = null
      }
      const lineHeight = Number.parseFloat(window.getComputedStyle(el).lineHeight)
      const minHeight = lineHeight * 3
      const viewport = window.visualViewport
      // bottom = 可視領域の下端（フォーカス時に操作行が画面外かの判定用）
      // available = 入力欄が使ってよい高さの上限（スクロール位置に依存しない）
      let bottom = viewport ? viewport.offsetTop + viewport.height : window.innerHeight
      let available = viewport ? viewport.height : window.innerHeight
      const ancestorScrollPositions: Array<{
        element: HTMLElement
        scrollLeft: number
        scrollTop: number
      }> = []
      // 現在の DefaultLayout には内側スクロール領域が存在しない（.layout-main は
      // overflow-x:auto のため computed overflow-y も auto になるが、flex:1 かつ
      // min-height:100vh で内容高に伸びるため実際にはスクロールしない）。
      // 将来パネルをスクロール領域に入れ子にしたときのための一般化として残す。
      for (let parent = el.parentElement; parent; parent = parent.parentElement) {
        const { overflowY } = window.getComputedStyle(parent)
        if (
          /(auto|scroll)/.test(overflowY) ||
          (/(hidden|clip)/.test(overflowY) && parent.scrollHeight > parent.clientHeight)
        ) {
          bottom = Math.min(bottom, parent.getBoundingClientRect().bottom)
          if (parent.clientHeight > 0) available = Math.min(available, parent.clientHeight)
          if (
            parent.scrollHeight > parent.clientHeight ||
            parent.scrollWidth > parent.clientWidth
          ) {
            ancestorScrollPositions.push({
              element: parent,
              scrollLeft: parent.scrollLeft,
              scrollTop: parent.scrollTop,
            })
          }
        }
      }
      const windowScrollPosition = { x: window.scrollX, y: window.scrollY }
      const restoreParentScrollPositions = () => {
        ancestorScrollPositions.forEach(({ element, scrollLeft, scrollTop }) => {
          element.scrollLeft = scrollLeft
          element.scrollTop = scrollTop
        })
        if (
          window.scrollX !== windowScrollPosition.x ||
          window.scrollY !== windowScrollPosition.y
        ) {
          window.scrollTo(windowScrollPosition.x, windowScrollPosition.y)
        }
      }
      // 送信ボタン・エラー行（mt-2 + 操作行の高さ。エラー文の折り返しも実測に含まれる）は
      // 常に可視域に残す。個別プロパティを getComputedStyle で読むより余白変更に強い。
      const actions = composerActionsRef.current
      const textareaRect = el.getBoundingClientRect()
      const reservedBelow = actions
        ? actions.getBoundingClientRect().bottom - textareaRect.bottom
        : 0
      // 最低3行は維持する。初めから画面外の場合は、通常のフォーカス移動で表示できる。
      const maxHeight = Math.max(
        minHeight,
        Math.min(lineHeight * 30, available - reservedBelow - COMPOSER_BOTTOM_MARGIN)
      )
      const scrollTop = el.scrollTop
      el.style.height = "auto"
      const contentHeight = el.scrollHeight
      el.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`
      el.style.overflowY = "auto"
      // 末尾編集中は入力・貼り付け後もcaretを表示する。入力に無関係な再レンダー
      // （SWRの再検証など）では閲覧位置を保つ。
      el.scrollTop =
        followCaret && document.activeElement === el && el.selectionEnd === el.value.length
          ? el.scrollHeight
          : scrollTop
      // xl のケース詳細では左カラム自体がスクロール領域になる。textarea の高さ変更時に
      // ブラウザが caret を追って親カラムをスクロールすると、上辺がチャット履歴側へ動いて
      // 見えるため、入力開始時の親スクロール位置を同期・次フレームの両方で戻す。
      restoreParentScrollPositions()
      if (followCaret) {
        restoreScrollFrameRef.current = window.requestAnimationFrame(() => {
          restoreScrollFrameRef.current = null
          restoreParentScrollPositions()
        })
      }
      return bottom
    }

    resizeComposerRef.current = resize
    const handleViewportResize = () => resize(false)
    window.addEventListener("resize", handleViewportResize)
    window.visualViewport?.addEventListener("resize", handleViewportResize)
    // 初めから操作行が画面外の場合は、編集開始時に入力欄と送信操作をまとめて可視化する。
    // 高さの上限はスクロール位置に依存しないため、スクロール後の再計算は不要。
    // 入力中の再計算では親をスクロールさせない。
    const focus = () => {
      const bottom = resize(false)
      const actions = composerActionsRef.current
      if (actions && actions.getBoundingClientRect().bottom > bottom) {
        actions.scrollIntoView({ block: "nearest", behavior: "auto" })
      }
    }
    el.addEventListener("focus", focus)
    return () => {
      if (restoreScrollFrameRef.current != null) {
        window.cancelAnimationFrame(restoreScrollFrameRef.current)
        restoreScrollFrameRef.current = null
      }
      window.removeEventListener("resize", handleViewportResize)
      window.visualViewport?.removeEventListener("resize", handleViewportResize)
      el.removeEventListener("focus", focus)
    }
  }, [])

  // エラー・接触内訳・要返信トグルなども含め、DOM更新後の配置から再計算する。
  // 末尾追従は本文が変わったときだけ行い、無関係な再レンダーでは入力欄の閲覧位置を保つ。
  // イベントリスナーは上のeffectでmount時に一度だけ登録する。
  useLayoutEffect(() => {
    const draftChanged = previousDraftRef.current !== draft
    previousDraftRef.current = draft
    resizeComposerRef.current(draftChanged)
  })

  // 再読み込み時も未送信の current ドラフトを編集・送信できるよう復元する。
  // 初回の履歴取得完了時にだけ判定し、オペレーターが既に手入力している場合は上書きしない。
  useEffect(() => {
    if (isHistoryLoading || hasAttemptedDraftRestoreRef.current) return

    hasAttemptedDraftRestoreRef.current = true
    if (activeDraft || draft.trim().length > 0) return

    const currentDraft = drafts.find(
      (entry) => entry.status === "current" && !discardedDraftIds.has(entry.id) &&
        !entry.generationMetadata.humanReviewRequired
    )
    if (!currentDraft) return

    setActiveDraft(currentDraft)
    setDraft(currentDraft.sendCandidateBody ?? currentDraft.body)
    setIsClericalNotice(false)
  }, [activeDraft, discardedDraftIds, draft, drafts, isHistoryLoading])

  const isBusy = isSending || isSendingDraft || isDiscarding || isSubmittingDraft
  const automaticallyNonCountingReason =
    status === "closed"
      ? "クローズ済み"
      : status === "paused"
        ? "保留中"
        : status === "resolved"
          ? "遅延解消済み"
          : status === "monitoring"
            ? "ウォッチ中"
            : "約束期間中"
  // LINE ブロック中はカウント対象の督促送信だけ止める（サーバも 422 で拒否する）。事務連絡は送れる
  const countedSendBlockedByLine = lineBlocked && !automaticallyNonCounting && !isClericalNotice
  const sendDisabledReason = unsupportedStatus
    ? `ステータス「${status}」のケースには送信できません`
    : draftBlockedByNonCounting
      ? status === "closed" || isClericalOnlyStatus
        ? `${automaticallyNonCountingReason}のケースではAIドラフトを送信できません。通常メッセージとして送信してください`
        : "約束中・保留中はAIドラフトを送信できません。通常メッセージとして送信してください"
      : countedSendBlockedByLine
        ? "LINEブロック中のユーザーには督促送信できません"
        : null
  const sendDisabled =
    isBusy || isGenerating || draft.trim().length === 0 || sendDisabledReason != null
  const draftGenerationDisabled =
    isGenerating || isBusy || automaticallyNonCounting || unsupportedStatus

  const latestInboundMessage = messages
    .filter((message) => message.direction === "inbound")
    .reduce<DunningChatMessage | null>((latest, message) => {
      if (!latest) return message
      if (dayjs(message.sentAt).isAfter(dayjs(latest.sentAt))) return message
      if (dayjs(message.sentAt).isBefore(dayjs(latest.sentAt))) return latest
      return message.messageId > latest.messageId ? message : latest
    }, null)

  const awaitingReplyActive = caseItem.facts?.awaitingReply?.active === true
  const awaitingReplyToggleVisible =
    latestInboundMessage != null &&
    (awaitingReplyActive || caseItem.facts?.awaitingReply?.manuallyHandled === true)

  const handleToggleAwaitingReply = async () => {
    if (!latestInboundMessage) return
    const updated = await setAwaitingReplyActive(
      !awaitingReplyActive,
      awaitingReplyActive ? latestInboundMessage.messageId : undefined
    )
    if (updated) onSent?.()
  }

  const handleGenerate = async () => {
    if (unsupportedStatus) return
    clearSendError()
    setHumanReviewReason(null)
    const generated = await generate()
    if (!generated) return
    if ("outcome" in generated && generated.outcome === "human_review") {
      setHumanReviewReason(generated.reason)
      // 古いAI文面を新しい結果と誤認させない。手入力の文面は維持する。
      if (activeDraft) setDraft("")
      setActiveDraft(null)
      return
    }
    if ("outcome" in generated) return
    setActiveDraft(generated)
    setDraft(generated.sendCandidateBody ?? generated.body)
    // 新しいドラフトは既定（督促回数に数える）から始める。事務連絡にするかは担当者が送信前に選ぶ
    setIsClericalNotice(false)
  }

  // 破棄はサーバ（status: discarded）を正とする。成功時だけ画面のドラフト経路を解除し、
  // 入力欄の本文は残す（AI原稿を手直ししてから通常送信に切り替える動線を保つため）。
  // 破棄済み原稿は current から外れるので、再訪・再読み込み・遅延応答では復元されない。
  const discardDraft = async () => {
    if (!activeDraft || isBusy || isGenerating) return
    setIsDiscarding(true)
    try {
      if (!(await discard(activeDraft.id))) return
      setDiscardedDraftIds((current) => new Set(current).add(activeDraft.id))
      setActiveDraft(null)
    } finally {
      setIsDiscarding(false)
    }
  }

  const handleFeedback = async (draftId: number, feedback: "good" | "bad" | null) => {
    const updated = await setFeedback(draftId, feedback)
    // コンポーザーで使用中のドラフトへの評価なら、バーの表示状態も同期する
    // （await 中に activeDraft が差し替わることがあるため、最新の state から判定する）
    if (updated) {
      setActiveDraft((current) => (current?.id === updated.id ? updated : current))
    }
  }

  const submit = async () => {
    const body = draft.trim()
    if (sendDisabled) return

    if (activeDraft) {
      // ドラフト経路: 編集内容を保存（PATCH editedBody）してから送信（POST :id/send）
      // PATCH 中は isSendingDraft が立たないため、二重送信・送信中の破棄を isSubmittingDraft で塞ぐ
      setIsSubmittingDraft(true)
      try {
        const saved = await saveEditedBody(activeDraft.id, body)
        if (!saved) return
        const sent = await sendDraft(saved.id, isClericalNotice ? { countsAsContact: false } : undefined)
        if (!sent) return
        setDraft("")
        setActiveDraft(null)
        // 誤爆防止: 事務連絡フラグは送信ごとにリセット（デフォルトは常にカウントする側）
        setIsClericalNotice(false)
        await refreshMessages()
        onSent?.()
      } finally {
        setIsSubmittingDraft(false)
      }
      return
    }

    const nonCounting = automaticallyNonCounting || isClericalNotice
    const ok = await sendMessage(body, nonCounting ? { countsAsContact: false } : undefined)
    if (ok) {
      setDraft("")
      // 誤爆防止: 事務連絡フラグは送信ごとにリセット（デフォルトは常にカウントする側）
      setIsClericalNotice(false)
      await refreshDrafts()
      onSent?.()
    }
  }

  return (
    // 左ペイン内でパネルを下方向へ伸ばし、履歴の高さ・入力欄の上端を保つ。
    // 親ペインが独立スクロールを担うため、ここでは flex-1 で高さを固定しない。
    <div className="flex shrink-0 flex-col overflow-clip [overflow-anchor:none] rounded-xl border border-gray-200 bg-[#eef2f4] shadow-sm">
      <div className="sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="text-[1.3rem] font-bold text-black">💬 督促チャット（LINE）</span>
          {lineBlocked && (
            <span className="rounded-full border border-red-300 bg-red-100 px-2.5 py-0.5 text-[1.1rem] font-semibold text-red-700">
              LINEブロック中
            </span>
          )}
          {awaitingReplyToggleVisible && (
            <button
              type="button"
              role="switch"
              aria-checked={awaitingReplyActive}
              aria-label="要返信"
              disabled={isUpdatingAwaitingReply}
              onClick={handleToggleAwaitingReply}
              className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-[1.1rem] font-medium text-black disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span>要返信</span>
              <span
                aria-hidden="true"
                className={`relative h-5 w-9 rounded-full transition-colors ${
                  awaitingReplyActive ? "bg-red-500" : "bg-gray-300"
                }`}
              >
                <span
                  className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                    awaitingReplyActive ? "translate-x-[18px]" : "translate-x-0.5"
                  }`}
                />
              </span>
            </button>
          )}
          {/* チャットステータス（chat_rooms.status）: 現在値バッジ + 変更ドロップダウン */}
          <span className="ml-auto flex items-center gap-1.5">
            <span
              className={`rounded-full border px-2.5 py-0.5 text-[1.1rem] font-semibold ${
                CHAT_STATUS_CLASSES[chatStatus ?? "_none"]
              }`}
            >
              {CHAT_STATUS_LABELS[chatStatus ?? "_none"]}
            </span>
            <select
              value={chatStatus ?? "_none"}
              disabled={chatStatus == null || isUpdatingChatStatus}
              onChange={(event) => updateChatStatus(event.target.value as ChatRoomStatus)}
              title="チャットステータスを変更"
              className="cursor-pointer rounded-md border border-gray-300 bg-white px-1.5 py-0.5 text-[1.1rem] text-black outline-none focus:border-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              {CHAT_STATUS_ORDER.map((value) => (
                <option key={value} value={value}>
                  {CHAT_STATUS_LABELS[value]}
                </option>
              ))}
            </select>
          </span>
          {todayCount != null && (
            <span className="rounded-full bg-gray-100 px-3 py-0.5 text-[1.15rem] font-medium tabular-nums text-black">
              本日接触 {todayCount}/{DAILY_CONTACT_LIMIT}
            </span>
          )}
        </div>
        {isClericalOnlyStatus && (
          <p className="mt-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[1.15rem] font-medium text-blue-800">
            {status === "resolved"
              ? "遅延解消済み。送信は事務連絡として扱われ、督促回数にカウントされません"
              : "ウォッチ中。現在は遅延していないため、送信は事務連絡として扱われ、督促回数にカウントされません"}
          </p>
        )}
        {(chatStatusError || updateAwaitingReplyError) && (
          <p className="mt-1 text-[1.1rem] text-red-600">
            {chatStatusError || updateAwaitingReplyError}
          </p>
        )}
        {/* 本日の督促接触の内訳（「10:01 電話」形式） */}
        {todayContacts.length > 0 && (
          <p className="mt-1 text-[1.1rem] tabular-nums text-black">
            {todayContacts
              .map(
                (entry) =>
                  `${dayjs(entry.contactedAt).format("HH:mm")} ${
                    CONTACT_CHANNEL_LABELS[entry.channel] ?? entry.channel
                  }${todayContactMarker(entry)}`
              )
              .join(" ・ ")}
          </p>
        )}
      </div>

      {/* 履歴には画面の半分以上を確保し、長い入力文でも縮めない。
          入力欄が伸びた分はページ側をスクロールする。 */}
      <div
        ref={scrollRef}
        className="flex h-[max(320px,52dvh,calc(100dvh-52rem))] shrink-0 flex-col gap-3 overflow-y-auto overflow-x-hidden px-4 py-5"
      >
        {hasOlder && (
          <button
            type="button"
            onClick={() => {
              const el = scrollRef.current
              if (el)
                historyScroll.current = {
                  first: messages[0]?.messageId,
                  height: el.scrollHeight,
                  top: el.scrollTop,
                }
              void loadOlder()
            }}
            disabled={loadingOlder}
            className="self-center rounded-lg border bg-white px-4 py-2"
          >
            {loadingOlder ? "読込中…" : "以前のメッセージを読み込む"}
          </button>
        )}
        {olderError && (
          <p role="alert" className="text-red-700">
            {olderError}
          </p>
        )}
        {isLoading && <p className="py-8 text-center text-[1.3rem] text-black">読み込み中…</p>}
        {error && !isLoading && (
          <p className="py-8 text-center text-[1.3rem] text-red-600">
            チャット履歴の取得に失敗しました。
          </p>
        )}
        {!isLoading && !error && messages.length === 0 && (
          <p className="py-8 text-center text-[1.3rem] text-black">メッセージはまだありません。</p>
        )}
        {messages.map((message) => (
          <Bubble key={message.messageId} message={message} />
        ))}
      </div>

      <div className="shrink-0 border-t border-gray-200 bg-white px-5 py-4">
        {/* AIドラフト: 未使用時は生成ボタンの1行、使用中は操作をまとめたカードに集約 */}
        {!activeDraft ? (
          <div className="mb-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={draftGenerationDisabled}
              className="rounded-full border border-[#bfe8f1] bg-[#e6f6fa] px-4 py-1.5 text-[1.25rem] font-semibold text-[#177a92] transition-colors hover:bg-[#d3eff5] disabled:opacity-50"
            >
              {isGenerating ? "🤖 文面を生成中…" : "🤖 AIで文面を準備する"}
            </button>
            <span className="text-[1.15rem] text-black">
              会話とケースの状態に合わせて返答・督促文をドラフトします
            </span>
          </div>
        ) : (
          <div
            className="mb-3 rounded-xl border border-[#bfe8f1] bg-[#f4fbfd] px-4 py-3"
            title={activeDraft.model ? `生成モデル: ${activeDraft.model}` : undefined}
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[1.25rem] font-bold text-[#177a92]">🤖 AIドラフト</span>
              <span
                className={`rounded-full px-2 py-0.5 text-[1.05rem] font-semibold ${DRAFT_STATUS_CLASSES.current}`}
              >
                未送信
              </span>
              <span className="ml-auto flex items-center gap-3 text-[1.15rem]">
                <DraftFeedbackButtons
                  draft={activeDraft}
                  disabled={isBusy}
                  onSelect={(feedback) => handleFeedback(activeDraft.id, feedback)}
                />
                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={draftGenerationDisabled}
                  className="font-medium text-primary hover:opacity-70 disabled:opacity-50"
                >
                  {isGenerating ? "生成中…" : "↻ 別の文面"}
                </button>
                <button
                  type="button"
                  onClick={discardDraft}
                  disabled={isBusy || isGenerating}
                  className="text-black hover:text-black disabled:opacity-50"
                >
                  ✕ 破棄
                </button>
              </span>
            </div>
            <p className="mt-1.5 text-[1.05rem] text-black">
              下の入力欄でそのまま編集できます。編集して送信すると改善用データとして記録されます。
            </p>
            <DraftDecisionLog
              decision={activeDraft.generationMetadata.decision}
              observation={activeDraft.generationMetadata.decisionObservation}
            />
          </div>
        )}
        {draftError && (
          <p className="mb-2 text-[1.15rem] text-red-600">
            {draftError}
            {/* 有効プロンプトなし（422）→ AIプロンプト設定ページへ誘導 */}
            {draftError.includes("有効なシステムプロンプトがありません") && (
              <>
                {" "}
                <Link
                  href="/admin/dunning-prompt-settings"
                  className="underline hover:opacity-70"
                  prefetch={false}
                >
                  AIプロンプト設定ページ
                </Link>
                から登録してください。
              </>
            )}
          </p>
        )}

        {displayedHumanReviewReason && (
          <div role="alert" className="mb-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900">
            <p className="font-semibold">人間による対応が必要です</p>
            <p>AIドラフトは生成していません。{displayedHumanReviewReason}</p>
            <DraftDecisionLog
              decision={humanReview?.decision}
              observation={humanReview?.decisionObservation}
            />
          </div>
        )}

        {/* AIドラフト使用中は枠色で「ドラフトを編集している」ことを視覚化する */}
        <div
          className={`rounded-xl border px-4 py-3 focus-within:border-primary ${
            activeDraft ? "border-[#7fcbdd] bg-[#fbfeff]" : "border-gray-300 bg-white"
          }`}
        >
          <textarea
            ref={composerRef}
            rows={3}
            className="w-full resize-none bg-transparent text-[1.35rem] leading-relaxed text-black outline-none"
            placeholder={
              activeDraft
                ? "AIドラフトを編集（⌘+Enterで送信）"
                : "メッセージを入力（⌘+Enterで送信）"
            }
            value={draft}
            disabled={isBusy || unsupportedStatus}
            onChange={(event) => {
              setDraft(event.target.value)
              if (sendError) clearSendError()
            }}
            onKeyDown={(event) => {
              if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
                event.preventDefault()
                submit()
              }
            }}
          />
          <div ref={composerActionsRef} className="mt-2 flex items-center gap-3">
            {/* AIドラフトでも電話後の案内など架電内容の確認送信があるため、ドラフト使用中も選べる。
                自動で非カウントになるケース（ドラフトは送れない）と未対応ステータスでは出さない */}
            {!automaticallyNonCounting && !unsupportedStatus && (
              <label
                className="flex min-w-0 cursor-pointer items-center gap-2 text-[1.2rem] text-black"
                title="電話でお伝えした内容の確認送信など。新規の督促接触として扱われません"
              >
                <input
                  type="checkbox"
                  checked={isClericalNotice}
                  disabled={isBusy}
                  onChange={(event) => setIsClericalNotice(event.target.checked)}
                  className="h-4 w-4 shrink-0 cursor-pointer appearance-auto [-webkit-appearance:checkbox] accent-primary"
                />
                架電内容の事務連絡（督促回数に数えない）
              </label>
            )}
            {(sendError || sendDisabledReason) && (
              <span
                className={`min-w-0 flex-1 text-[1.15rem] ${sendError ? "text-red-600" : "text-black"}`}
              >
                {sendError || sendDisabledReason}
              </span>
            )}
            {activeDraft?.generationMetadata.humanReviewRequired && (
              <span className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[1.1rem] font-semibold text-amber-700">
                要レビュー
              </span>
            )}
            {draftContentNotice && (
              <span
                title={draftContentNotice}
                className="rounded-full border border-amber-300 bg-amber-50 px-3 py-1 text-[1.1rem] font-semibold text-amber-700"
              >
                金額・日付を要確認
              </span>
            )}
            {activeDraft?.generationMetadata.humanFollowRecommended && (
              <span
                className="cursor-help rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-[1.1rem] font-semibold text-sky-700"
                title={`自動送信しません。${activeDraft.generationMetadata.humanFollowReason ?? ""}`}
              >
                人の対応推奨
              </span>
            )}
            {activeDraft?.generationMetadata.callFollowUp && (
              <span
                className="cursor-help rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-[1.1rem] font-semibold text-sky-700"
                title="自動送信しません。電話でお伝えした内容と合っているか確認してから送ってください。"
              >
                電話後の案内
              </span>
            )}
            <button
              type="button"
              onClick={submit}
              disabled={sendDisabled}
              className={`ml-auto shrink-0 rounded-[1.2rem] px-6 py-2 text-[1.35rem] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50 ${
                isClericalNotice || (automaticallyNonCounting && !activeDraft)
                  ? "bg-gray-600"
                  : "bg-primary"
              }`}
            >
              {isBusy
                ? "送信中…"
                : activeDraft
                  ? isClericalNotice
                    ? "AIドラフトを事務連絡として送信"
                    : "AIドラフトを送信"
                  : automaticallyNonCounting
                    ? status === "closed" || isClericalOnlyStatus
                      ? "事務連絡として送信"
                      : "非カウントで送信"
                    : isClericalNotice
                      ? "事務連絡として送信"
                      : "送信"}
            </button>
          </div>
        </div>
        <p className="mt-2 text-[1.1rem] text-black">
          {automaticallyNonCounting && !activeDraft
            ? `${automaticallyNonCountingReason}のため督促回数には数えません。LINEで非カウント送信されます。`
            : isClericalNotice
              ? "事務連絡として LINE で配信されます。接触回数にカウントされず、本日の接触上限ゲートも適用されません。"
              : "送信すると LINE で配信され、本日の接触回数にカウントされます。"}
        </p>

        {/* ドラフト履歴（superseded / sent 含む・新しい順） */}
        {drafts.length > 0 && (
          <details className="mt-2">
            <summary className="cursor-pointer text-[1.2rem] font-semibold text-black hover:text-black">
              AIドラフト履歴（{drafts.length}件）
            </summary>
            <ul className="mt-2 max-h-[200px] divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200">
              {drafts.map((entry) => (
                <li
                  key={entry.id}
                  className="px-3 py-3"
                  title={entry.model ? `生成モデル: ${entry.model}` : undefined}
                >
                  <div className="flex flex-wrap items-center gap-2 text-[1.05rem]">
                    <span
                      className={`rounded-full px-2 py-0.5 font-semibold ${DRAFT_STATUS_CLASSES[entry.status]}`}
                    >
                      {DRAFT_STATUS_LABELS[entry.status] ?? entry.status}
                    </span>
                    <span className="tabular-nums text-black">
                      {formatJapaneseDateTime(entry.createdAt)}
                    </span>
                    {entry.sentAt && (
                      <span className="tabular-nums text-black">
                        送信 {formatJapaneseDateTime(entry.sentAt)}
                      </span>
                    )}
                    <span className="ml-auto">
                      <DraftFeedbackButtons
                        draft={entry}
                        onSelect={(feedback) => handleFeedback(entry.id, feedback)}
                      />
                    </span>
                  </div>
                  <p className="mt-2 max-h-[320px] overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-gray-50 px-3 py-2 text-[1.2rem] leading-relaxed text-black">
                    {entry.sendCandidateBody ?? entry.body}
                  </p>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </div>
  )
}
